import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, AlertTriangle } from "lucide-react";

interface Conversation {
  id: string;
  otherUserName: string;
  lastMessage: string | null;
}

interface ReportUserDialogProps {
  isOpen: boolean;
  onClose: () => void;
  reportedUserId: string;
  reportedUserName: string;
}

const REPORT_CATEGORIES = [
  { value: "harassment", label: "Harassment or bullying" },
  { value: "inappropriate_content", label: "Inappropriate content" },
  { value: "fraud", label: "Fraud or scam" },
  { value: "off_platform_transaction", label: "Off-platform transaction" },
  { value: "academic_dishonesty", label: "Academic dishonesty" },
  { value: "impersonation", label: "Impersonation" },
  { value: "spam", label: "Spam or solicitation" },
  { value: "safety_concern", label: "Safety concern" },
  { value: "other", label: "Other violation" },
];

export function ReportUserDialog({
  isOpen,
  onClose,
  reportedUserId,
  reportedUserName,
}: ReportUserDialogProps) {
  const { user } = useAuth();
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [selectedThreads, setSelectedThreads] = useState<string[]>([]);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingConversations, setLoadingConversations] = useState(false);

  useEffect(() => {
    if (isOpen && user) {
      fetchConversationsWithUser();
    }
  }, [isOpen, user, reportedUserId]);

  const fetchConversationsWithUser = async () => {
    if (!user) return;
    setLoadingConversations(true);
    try {
      // Fetch conversations where the current user and reported user are participants
      const { data, error } = await supabase
        .from("conversations")
        .select(`
          id,
          last_message,
          participant1_id,
          participant2_id,
          service_id,
          services(title)
        `)
        .or(`and(participant1_id.eq.${user.id},participant2_id.eq.${reportedUserId}),and(participant1_id.eq.${reportedUserId},participant2_id.eq.${user.id})`);

      if (error) throw error;

      const formattedConversations: Conversation[] = (data || []).map((conv: any) => ({
        id: conv.id,
        otherUserName: conv.services?.title || `Conversation`,
        lastMessage: conv.last_message,
      }));

      setConversations(formattedConversations);
    } catch (error) {
      console.error("Error fetching conversations:", error);
    } finally {
      setLoadingConversations(false);
    }
  };

  const handleSubmit = async () => {
    if (!user) {
      toast.error("You must be logged in to submit a report");
      return;
    }

    if (!category) {
      toast.error("Please select a category");
      return;
    }

    if (!description.trim() || description.trim().length < 20) {
      toast.error("Please provide a description (at least 20 characters)");
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.from("user_reports").insert({
        reported_user_id: reportedUserId,
        reporter_id: user.id,
        category,
        description: description.trim(),
        linked_thread_ids: selectedThreads,
      });

      if (error) throw error;

      toast.success("Report submitted successfully");
      handleClose();
    } catch (error: any) {
      console.error("Error submitting report:", error);
      toast.error(error.message || "Failed to submit report");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setCategory("");
    setDescription("");
    setSelectedThreads([]);
    onClose();
  };

  const toggleThread = (threadId: string) => {
    setSelectedThreads((prev) =>
      prev.includes(threadId)
        ? prev.filter((id) => id !== threadId)
        : [...prev, threadId]
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <AlertTriangle className="h-4 w-4 text-destructive" />
            Report User
          </DialogTitle>
          <DialogDescription className="text-xs">
            Report <span className="font-medium">{reportedUserName}</span> for violating community guidelines.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Category *</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="h-9 text-xs">
                <SelectValue placeholder="Select violation type" />
              </SelectTrigger>
              <SelectContent>
                {REPORT_CATEGORIES.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value} className="text-xs">
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Description *</Label>
            <Textarea
              placeholder="Describe the violation in detail (min. 20 characters)..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="min-h-[80px] text-xs resize-none"
              maxLength={1000}
            />
            <p className="text-[10px] text-muted-foreground text-right">
              {description.length}/1000
            </p>
          </div>

          {conversations.length > 0 && (
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Link Conversations (optional)</Label>
              <div className="max-h-24 overflow-y-auto border rounded-md p-2 space-y-1">
                {loadingConversations ? (
                  <p className="text-xs text-muted-foreground">Loading...</p>
                ) : (
                  conversations.map((conv) => (
                    <label
                      key={conv.id}
                      className="flex items-center gap-2 cursor-pointer hover:bg-muted/50 rounded px-1 py-0.5"
                    >
                      <Checkbox
                        checked={selectedThreads.includes(conv.id)}
                        onCheckedChange={() => toggleThread(conv.id)}
                        className="h-3 w-3"
                      />
                      <span className="text-xs truncate">
                        {conv.otherUserName}
                        {conv.lastMessage && (
                          <span className="text-muted-foreground ml-1">
                            - {conv.lastMessage.slice(0, 30)}...
                          </span>
                        )}
                      </span>
                    </label>
                  ))
                )}
              </div>
              <p className="text-[10px] text-muted-foreground">
                Select relevant conversations for admin review
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="ghost" size="sm" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleSubmit}
            disabled={loading || !category || description.trim().length < 20}
            className="bg-destructive hover:bg-destructive/90"
          >
            {loading && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
            Submit Report
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
